/**
 * SerialPortService — Web Serial API wrapper for USB-COM barcode scanners.
 *
 * Reads raw bytes from a virtual COM port (e.g., Datalogic QBT2400 in USB-COM mode),
 * accumulates them in a buffer, and emits complete barcodes when CR/LF is received.
 *
 * Requirements:
 * - Chrome/Edge 89+ with Web Serial API
 * - Secure context (HTTPS or localhost)
 */

export type SerialState = 'disconnected' | 'connecting' | 'connected' | 'error';

export class SerialPortService {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private buffer: string[] = [];
  private reading = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  state: SerialState = 'disconnected';

  /** Callback when a complete barcode is received (terminated by CR/LF) */
  onBarcode: ((barcode: string) => void) | null = null;

  /** Callback when connection state changes */
  onStateChange: ((state: SerialState) => void) | null = null;

  /** Check if Web Serial API is available */
  static isSupported(): boolean {
    return 'serial' in navigator;
  }

  /** Open the native port selection dialog and store the selected port */
  async requestPort(): Promise<void> {
    if (!SerialPortService.isSupported()) {
      throw new Error('Web Serial API not supported in this browser');
    }
    this.port = await navigator.serial.requestPort();
  }

  /** Connect to the selected port */
  async connect(baudRate = 9600): Promise<void> {
    if (!this.port) {
      throw new Error('No port selected. Call requestPort() first.');
    }

    this.setState('connecting');

    try {
      await this.port.open({
        baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none',
      });

      console.log(`[SerialPort] Connected @ ${baudRate} baud`);
      this.setState('connected');
      this.startReading();

      // Listen for disconnect
      this.port.addEventListener('disconnect', this.handleDisconnect);
    } catch (err) {
      console.error('[SerialPort] Connection failed:', err);
      this.setState('error');
      throw err;
    }
  }

  /** Disconnect from the port */
  async disconnect(): Promise<void> {
    this.clearReconnectTimer();
    this.reading = false;

    try {
      if (this.reader) {
        await this.reader.cancel();
        this.reader.releaseLock();
        this.reader = null;
      }
    } catch { /* reader may already be released */ }

    try {
      if (this.port) {
        this.port.removeEventListener('disconnect', this.handleDisconnect);
        await this.port.close();
      }
    } catch { /* port may already be closed */ }

    this.port = null;
    this.buffer = [];
    this.setState('disconnected');
  }

  /** Open dialog, select port, and connect in one step */
  async requestAndConnect(baudRate = 9600): Promise<void> {
    await this.requestPort();
    await this.connect(baudRate);
  }

  /** Connect to an already-granted port (no user gesture needed) */
  async connectToExistingPort(port: SerialPort, baudRate = 9600): Promise<void> {
    // Disconnect existing if any
    if (this.port && this.port !== port) {
      await this.disconnect();
    }
    this.port = port;
    await this.connect(baudRate);
  }

  // --- Internal ---

  private setState(state: SerialState) {
    this.state = state;
    this.onStateChange?.(state);
  }

  private async startReading() {
    if (!this.port?.readable) return;
    this.reading = true;
    console.log('[SerialPort] Starting read loop');

    while (this.reading && this.port?.readable) {
      try {
        this.reader = this.port.readable.getReader();
        try {
          while (this.reading) {
            const { value, done } = await this.reader.read();
            if (done) break;
            if (value) {
              console.log(`[SerialPort] Data received: ${value.length} bytes`);
              this.onDataReceived(value);
            }
          }
        } finally {
          this.reader.releaseLock();
          this.reader = null;
        }
      } catch (err) {
        if (this.reading) {
          console.error('[SerialPort] Read error:', err);
        }
      }
    }
  }

  /** Process raw bytes byte-by-byte — matches Flutter _onDataReceived pattern */
  private onDataReceived(data: Uint8Array) {
    for (let i = 0; i < data.length; i++) {
      const byte = data[i];
      // CR (0x0D) or LF (0x0A) = end of barcode
      if (byte === 0x0D || byte === 0x0A) {
        if (this.buffer.length > 0) {
          const barcode = this.buffer.join('').trim();
          this.buffer = [];
          if (barcode.length > 0) {
            console.log('[SerialPort] Barcode:', barcode);
            this.onBarcode?.(barcode);
          }
        }
      } else {
        this.buffer.push(String.fromCharCode(byte));
        // Buffer overflow protection (matches Flutter 500 byte limit)
        if (this.buffer.length > 500) {
          console.warn('[SerialPort] Buffer overflow — clearing');
          this.buffer = [];
        }
      }
    }
  }

  private handleDisconnect = () => {
    console.warn('[SerialPort] Port disconnected');
    this.reading = false;
    this.reader = null;
    this.buffer = [];
    this.setState('error');

    // Auto-reconnect after 3 seconds
    this.scheduleReconnect();
  };

  private scheduleReconnect() {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(async () => {
      if (this.state !== 'disconnected' && this.port) {
        console.log('[SerialPort] Attempting reconnect...');
        try {
          await this.connect();
        } catch {
          // Retry in 5 seconds
          this.scheduleReconnect();
        }
      }
    }, 3000);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
